// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bell, Mail, Send, Users, Calendar, Plus, Edit, Settings, MessageSquare, Target, TestTube } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { format } from "date-fns";
import { useEventNotificationTemplates } from "@/hooks/useEventNotificationTemplates";
import { NotificationTemplate } from "@/hooks/useNotificationTemplates";
import NotificationTemplateEditor from "@/components/admin/notifications/NotificationTemplateEditor";
import NotificationTemplateList from "@/components/admin/notifications/NotificationTemplateList";
import NotificationAnalyticsDashboard from "@/components/admin/notifications/NotificationAnalyticsDashboard";
import HeaderFooterManager from "@/components/admin/notifications/HeaderFooterManager";
import CustomEventUpdateModal from "@/components/admin/notifications/CustomEventUpdateModal";
import { useToast } from "@/hooks/use-toast";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

const NotificationsPage = () => {
  const [activeTab, setActiveTab] = useState("templates");
  const [notifications, setNotifications] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSent: 0,
    openRate: 0,
    activeEvents: 0,
    subscribers: 0
  });
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);
  const [isTemplateEditorOpen, setIsTemplateEditorOpen] = useState(false);
  const [isCustomUpdateOpen, setIsCustomUpdateOpen] = useState(false);
  const [isTemplateTypeDialogOpen, setIsTemplateTypeDialogOpen] = useState(false);
  const [selectedTriggerEvent, setSelectedTriggerEvent] = useState<string>('');
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
  const [testingTriggerEvent, setTestingTriggerEvent] = useState<string>('');
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const { profile } = useAuth();
  const { toast } = useToast();
  const { 
    templates, 
    isLoading: templatesLoading, 
    createTemplate, 
    updateTemplate, 
    deleteTemplate,
    duplicateTemplate 
  } = useEventNotificationTemplates();

  useEffect(() => {
    if (profile?.role === 'admin') {
      fetchNotifications();
      fetchStats();
      fetchEvents();
    }
  }, [profile]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      // Admin can view all notifications related to events
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('related_module', 'events')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching notifications:', error);
      } else {
        setNotifications(data || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // Get total sent notifications
      const { count: totalSent } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('related_module', 'events')
        .eq('sent', true);

      // Get read notifications for open rate calculation
      const { count: readCount } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('related_module', 'events')
        .eq('read', true);

      // Get active events count
      const { count: activeEvents } = await supabase
        .from('events')
        .select('*', { count: 'exact', head: true })
        .gte('start_date', new Date().toISOString().split('T')[0]);

      // Get total coaches (subscribers)
      const { count: subscribers } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('role', 'coach');

      setStats({
        totalSent: totalSent || 0,
        openRate: totalSent > 0 ? Math.round(((readCount || 0) / totalSent) * 100) : 0,
        activeEvents: activeEvents || 0,
        subscribers: subscribers || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('id, name, start_date, end_date, status')
        .eq('is_active', true)
        .order('start_date', { ascending: true });

      if (error) {
        console.error('Error fetching events:', error);
      } else {
        setEvents(data || []);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const filteredNotifications = (notifications || []).filter(notification => {
    switch (activeTab) {
      case 'sent':
        return notification.sent;
      case 'drafts':
        return !notification.sent;
      default:
        return true;
    }
  });

  // Template management handlers
  const handleEditTemplate = (template: NotificationTemplate) => {
    setEditingTemplate(template);
    setIsTemplateEditorOpen(true);
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setIsTemplateEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setIsTemplateEditorOpen(false);
    setEditingTemplate(null);
  };

  const handleSaveTemplate = async (templateData: Partial<NotificationTemplate>) => {
    if (editingTemplate) {
      await updateTemplate(editingTemplate.id, templateData);
    } else {
      await createTemplate(templateData as Omit<NotificationTemplate, 'id' | 'created_at' | 'updated_at'>);
    }
  };

  const handleDuplicateTemplate = async (template: NotificationTemplate) => {
    await duplicateTemplate(template);
  };

  const handleDeleteTemplate = async (id: string) => {
    await deleteTemplate(id);
  };

  // Get active templates for automated notifications
  const getActiveTemplate = (triggerEvent: string) => {
    return (templates || []).find(t => t.trigger_event === triggerEvent && t.is_active);
  };

  const handleEditAutomatedNotification = (triggerEvent: string) => {
    setSelectedTriggerEvent(triggerEvent);
    setIsTemplateTypeDialogOpen(true);
  };

  const handleCreateTemplateForType = (type: 'email' | 'sms') => {
    const triggerEvent = selectedTriggerEvent;
    console.log('Creating template for:', triggerEvent, 'type:', type);
    
    // Look for existing template of this type
    const existingTemplate = (templates || []).find(t => 
      t.trigger_event === triggerEvent && t.type === type
    );
    
    if (existingTemplate) {
      handleEditTemplate(existingTemplate);
    } else {
      // Create a new template
      const templateName = `${triggerEvent.replace(/_/g, ' ')} (${type.toUpperCase()})`;
      const newTemplate: Partial<NotificationTemplate> = {
        title: templateName,
        type: type,
        audience: 'athlete',
        default_copy: type === 'sms' 
          ? `Event update: {{event_name}} on {{event_date}} at {{venue}}. Details: {{details}}`
          : `Dear {{participant_name}},\n\nThis is regarding {{event_name}} scheduled for {{event_date}} at {{venue}}.\n\n{{details}}\n\nBest regards,\nRecruitLook Team`,
        trigger_event: triggerEvent,
        delivery_channel: type,
        is_active: true,
        category: 'events'
      };
      setEditingTemplate(newTemplate as NotificationTemplate);
      setIsTemplateEditorOpen(true);
    }
    setIsTemplateTypeDialogOpen(false);
  };

  const handleTestNotification = (triggerEvent: string) => {
    setTestingTriggerEvent(triggerEvent);
    setIsTestDialogOpen(true);
  };

  const sendTestNotification = async (type: 'email' | 'sms') => {
    console.log(`🧪 Starting test ${type} notification process`);
    console.log(`🧪 Testing trigger event: ${testingTriggerEvent}`);
    console.log(`🧪 Available templates:`, templates);
    
    try {
      const template = (templates || []).find(t => 
        t.trigger_event === testingTriggerEvent && t.type === type
      );
      
      console.log(`🧪 Found template for ${type}:`, template);
      
      if (!template) {
        console.warn(`❌ No ${type} template found for trigger: ${testingTriggerEvent}`);
        toast({
          title: "No template found",
          description: `Please create a ${type} template for ${testingTriggerEvent.replace(/_/g, ' ')} first.`,
          variant: "destructive"
        });
        return;
      }

      // Validate required inputs
      if (type === 'email' && !testEmail) {
        console.error('❌ Email validation failed: No email address provided');
        toast({
          title: "Email required",
          description: "Please enter a valid email address.",
          variant: "destructive"
        });
        return;
      }

      if (type === 'sms' && !testPhone) {
        console.error('❌ SMS validation failed: No phone number provided');
        toast({
          title: "Phone required", 
          description: "Please enter a valid phone number.",
          variant: "destructive"
        });
        return;
      }

      // Enhanced email validation
      if (type === 'email' && (!testEmail.includes('@') || !testEmail.includes('.'))) {
        console.error('❌ Email validation failed: Invalid email format:', testEmail);
        toast({
          title: "Invalid email format",
          description: "Please enter a valid email address (e.g., user@example.com).",
          variant: "destructive"
        });
        return;
      }

      // Call the appropriate edge function
      let response;
      if (type === 'email') {
        console.log('📧 Preparing email test payload...');
        
        // Map trigger event to appropriate email type
        const emailTypeMapping: Record<string, string> = {
          'event_update': 'event_notification',
          'schedule_change': 'event_notification', 
          'weather_update': 'event_notification',
          'event_reminder': 'event_notification',
          'custom_update': 'event_notification',
          'team_registration': 'event_notification',
          'individual_registration': 'event_notification',
          'roster_submitted': 'event_notification',
          'roster_missing': 'event_notification',
          'checkin_activity': 'event_notification',
          'payment_completed': 'event_notification',
          'payment_reminder': 'payment_receipt',
          'registration_confirmed': 'registration_confirmation'
        };

        const emailType = emailTypeMapping[testingTriggerEvent] || 'event_notification';
        
        // Create payload that exactly matches Edge Function expectations
        const emailPayload = {
          email_type: emailType,
          to_email: testEmail,
          data: {
            event_name: `Test Event - ${testingTriggerEvent.replace(/_/g, ' ')}`,
            name: 'Test User',
            subject: template.title || 'Test Notification',
            message: template.default_copy || 'This is a test notification to verify email delivery.',
            event_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 1 week from now
            venue_name: 'Test Venue',
            venue_city: 'Test City',
            venue_state: 'Test State',
            checkin_time: '8:00 AM',
            days_until: '7',
            weather_conditions: 'Partly cloudy, 72°F',
            old_date: testingTriggerEvent === 'schedule_change' ? new Date().toISOString() : undefined,
            organization_name: 'RecruitLook',
            timestamp: new Date().toISOString(),
            notification_type: testingTriggerEvent,
            action_required: false,
            test_mode: true
          }
        };

        console.log('📧 Email payload being sent:', JSON.stringify(emailPayload, null, 2));
        
        // Validate payload structure before sending
        const validEmailTypes = ['coach_notification', 'registration_confirmation', 'payment_receipt', 'roster_deadline_reminder', 'admin_notification', 'event_notification', 'event_update', 'schedule_change', 'weather_update', 'reminder', 'custom_notification'];
        if (!validEmailTypes.includes(emailPayload.email_type)) {
          throw new Error(`Invalid email_type: ${emailPayload.email_type}. Must be one of: ${validEmailTypes.join(', ')}`);
        }
        
        if (!emailPayload.to_email || !emailPayload.to_email.includes('@')) {
          throw new Error(`Invalid to_email: ${emailPayload.to_email}`);
        }
        
        if (!emailPayload.data || typeof emailPayload.data !== 'object') {
          throw new Error(`Invalid data object: ${JSON.stringify(emailPayload.data)}`);
        }

        console.log('📧 Payload validation passed, calling Edge Function...');

        response = await invokeEdgeFunction('send-email', {
          body: emailPayload
        });

        console.log('📧 Edge Function response received:', JSON.stringify({
          data: response.data,
          error: response.error,
          hasData: !!response.data,
          hasError: !!response.error
        }, null, 2));

      } else {
        console.log('📱 Preparing SMS test payload...');
        
        const smsPayload = {
          to_phone: testPhone,
          message: template.default_copy || `Test SMS notification for ${testingTriggerEvent.replace(/_/g, ' ')}`,
          from_phone: undefined // Will use default Twilio number
        };

        console.log('📱 SMS payload being sent:', JSON.stringify(smsPayload, null, 2));

        response = await invokeEdgeFunction('send-sms', {
          body: smsPayload
        });

        console.log('📱 SMS Edge Function response received:', {
          data: response.data,
          error: response.error,
          hasData: !!response.data,
          hasError: !!response.error
        });
      }

      // Enhanced error handling - capture Edge Function response details
      if (response.error) {
        console.error(`❌ ${type} sending failed with detailed error:`, JSON.stringify({
          message: response.error.message,
          details: response.error.details,
          hint: response.error.hint,
          code: response.error.code,
          stack: response.error.stack,
          fullError: response.error
        }, null, 2));
        
        // Try to extract actual error message from the Edge Function response
        let actualErrorMessage = 'Unknown error occurred';
        
        // For FunctionsHttpError, we need to get the actual response
        if (response.error.name === 'FunctionsHttpError') {
          console.log('🔍 FunctionsHttpError detected - this indicates the Edge Function returned an error response');
          actualErrorMessage = 'Edge Function returned an error. Check Edge Function logs for details.';
        }
        
        // Provide specific error messages based on error content
        let userErrorMessage = actualErrorMessage;
        
        if (actualErrorMessage.includes('SENDGRID_API_KEY') || actualErrorMessage.includes('Email service not configured')) {
          userErrorMessage = 'Email service not configured. Missing SENDGRID_API_KEY in Supabase secrets.';
        } else if (actualErrorMessage.includes('403') || actualErrorMessage.includes('Forbidden')) {
          userErrorMessage = 'Email service permission denied. Please verify your domain in Resend dashboard.';
        } else if (actualErrorMessage.includes('validation') || actualErrorMessage.includes('Invalid')) {
          userErrorMessage = `Validation error: ${actualErrorMessage}`;
        } else if (actualErrorMessage.includes('network') || actualErrorMessage.includes('timeout')) {
          userErrorMessage = 'Network error. Please check your connection and try again.';
        } else if (response.error.message && response.error.message !== 'Edge Function returned a non-2xx status code') {
          userErrorMessage = response.error.message;
        }
        
        toast({
          title: `Failed to send test ${type}`,
          description: userErrorMessage,
          variant: "destructive"
        });
        return;
      }

      // Success handling
      console.log(`✅ Test ${type} sent successfully:`, response.data);
      
      toast({
        title: `Test ${type} sent successfully!`,
        description: `Test ${type} sent to ${type === 'email' ? testEmail : testPhone} using "${template.title}" template.`
      });
      
    } catch (error: any) {
      console.error(`❌ Comprehensive error in test ${type} process:`, {
        message: error.message,
        stack: error.stack,
        name: error.name,
        cause: error.cause,
        fullError: error
      });
      
      // Enhanced user error messages
      let userMessage = 'An unexpected error occurred. Please check the console for details.';
      
      if (error.message?.includes('network') || error.name === 'NetworkError') {
        userMessage = 'Network connection error. Please check your internet connection.';
      } else if (error.message?.includes('JSON') || error.message?.includes('parse')) {
        userMessage = 'Data format error. Please try again or contact support.';
      } else if (error.message) {
        userMessage = error.message;
      }
      
      toast({
        title: `Failed to send test ${type}`,
        description: userMessage,
        variant: "destructive"
      });
    }
    
    setIsTestDialogOpen(false);
  };

  const renderNotificationsList = () => {
    if (loading) {
      return (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading notifications...</p>
        </div>
      );
    }

    if (filteredNotifications.length === 0) {
      return (
        <div className="text-center py-12">
          <Bell className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {activeTab === 'sent' ? 'No sent notifications' : 
             activeTab === 'drafts' ? 'No draft notifications' : 'No notifications yet'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {activeTab === 'sent' ? 'Sent notifications will appear here' :
             activeTab === 'drafts' ? 'Draft notifications will be saved here' :
             'All event notifications will appear here'}
          </p>
          <Button onClick={handleCreateTemplate}>
            <Plus className="h-4 w-4 mr-2" />
            Create Notification
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {filteredNotifications.map((notification) => (
          <div
            key={notification.id}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-semibold text-gray-900">{notification.title}</h4>
              <div className="flex items-center gap-2">
                {notification.sent ? (
                  <Badge variant="success">Sent</Badge>
                ) : (
                  <Badge variant="secondary">Draft</Badge>
                )}
                <span className="text-sm text-gray-500">
                  {format(new Date(notification.created_at), 'MMM d, yyyy')}
                </span>
              </div>
            </div>
            <p className="text-gray-700 mb-2">{notification.message}</p>
            <div className="flex justify-between items-center text-sm text-gray-500">
              <span>
                Recipient: {notification.profiles?.name || notification.profiles?.email || 'Unknown'}
              </span>
              <span>
                {notification.read ? 'Read' : 'Unread'}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Debug logging
  console.log('NotificationsPage render - profile:', profile);
  console.log('NotificationsPage render - profile role:', profile?.role);
  console.log('NotificationsPage render - templates:', templates);
  console.log('NotificationsPage render - templatesLoading:', templatesLoading);

  // If no profile or not admin, show access denied
  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Loading...</h2>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (profile.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-gray-600">You must be an admin to access this page.</p>
          <p className="text-sm text-gray-500 mt-2">Current role: {profile.role}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Event Notifications
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage event-specific notifications, SMS alerts, and custom updates
          </p>
        </div>
        <Button onClick={() => setIsCustomUpdateOpen(true)} className="bg-orange-600 hover:bg-orange-700">
          <MessageSquare className="h-4 w-4 mr-2" />
          Send Custom Update
        </Button>
      </div>

      {/* Main Sections Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center space-y-0 pb-2">
            <CardTitle className="text-lg font-medium">Automated Event Notifications</CardTitle>
            <Bell className="h-5 w-5 ml-auto text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              System-triggered notifications for event registrations, payments, and reminders with SMS + Email delivery
            </p>
            <div className="space-y-2">
              <div 
                className="flex items-center justify-between text-sm p-3 rounded border hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => handleEditAutomatedNotification('registration_confirmed')}
              >
                <span className="font-medium">Registration & Payment confirmations</span>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1">
                    <Badge variant="outline" className="text-green-600 text-xs">Email</Badge>
                    <Badge variant="outline" className="text-blue-600 text-xs">SMS</Badge>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTestNotification('registration_confirmed');
                    }}
                  >
                    Test
                  </Button>
                  <Edit className="h-3 w-3 text-gray-400" />
                </div>
              </div>
              <div 
                className="flex items-center justify-between text-sm p-3 rounded border hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => handleEditAutomatedNotification('payment_reminder')}
              >
                <span className="font-medium">Payment reminders (initial + every 2 days)</span>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1">
                    <Badge variant="outline" className="text-green-600 text-xs">Email</Badge>
                    <Badge variant="outline" className="text-blue-600 text-xs">SMS</Badge>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTestNotification('payment_reminder');
                    }}
                  >
                    Test
                  </Button>
                  <Edit className="h-3 w-3 text-gray-400" />
                </div>
              </div>
              <div 
                className="flex items-center justify-between text-sm p-3 rounded border hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => handleEditAutomatedNotification('event_reminder')}
              >
                <span className="font-medium">Event reminders (1 week, 1 day)</span>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1">
                    <Badge variant="outline" className="text-green-600 text-xs">Email</Badge>
                    <Badge variant="outline" className="text-blue-600 text-xs">SMS</Badge>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTestNotification('event_reminder');
                    }}
                  >
                    Test
                  </Button>
                  <Edit className="h-3 w-3 text-gray-400" />
                </div>
              </div>
              <div 
                className="flex items-center justify-between text-sm p-3 rounded border hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => handleEditAutomatedNotification('event_cancelled')}
              >
                <span className="font-medium">Schedule & venue changes</span>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1">
                    <Badge variant="outline" className="text-green-600 text-xs">Email</Badge>
                    <Badge variant="outline" className="text-blue-600 text-xs">SMS</Badge>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTestNotification('event_cancelled');
                    }}
                  >
                    Test
                  </Button>
                  <Edit className="h-3 w-3 text-gray-400" />
                </div>
              </div>
              <div 
                className="flex items-center justify-between text-sm p-3 rounded border hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => handleEditAutomatedNotification('check_in_instructions')}
              >
                <span className="font-medium">Check-in instructions with QR codes</span>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1">
                    <Badge variant="outline" className="text-green-600 text-xs">Email</Badge>
                    <Badge variant="outline" className="text-blue-600 text-xs">SMS</Badge>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTestNotification('check_in_instructions');
                    }}
                  >
                    Test
                  </Button>
                  <Edit className="h-3 w-3 text-gray-400" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center space-y-0 pb-2">
            <CardTitle className="text-lg font-medium">Custom Event Updates</CardTitle>
            <Target className="h-5 w-5 ml-auto text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Send immediate updates for schedule changes, weather alerts, parking updates, and other event communications
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Active Events</span>
                <Badge variant="outline">{events.length}</Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Total Event Registrants</span>
                <Badge variant="outline">Ready to message</Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Delivery Channels</span>
                <div className="flex gap-1">
                  <Badge variant="outline" className="text-xs">Email</Badge>
                  <Badge variant="outline" className="text-xs">SMS</Badge>
                </div>
              </div>
            </div>
            <Button onClick={() => setIsCustomUpdateOpen(true)} className="w-full mt-4" size="sm">
              <MessageSquare className="h-4 w-4 mr-2" />
              Send Custom Update
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center space-y-0 pb-2">
            <CardTitle className="text-lg font-medium">Template Management</CardTitle>
            <Settings className="h-5 w-5 ml-auto text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Create and manage reusable notification templates with SMS + Email versions
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Email Templates</span>
                <Badge variant="outline">{(templates || []).length}</Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>SMS Templates</span>
                <Badge variant="outline">{(templates || []).filter(t => t.default_copy && t.default_copy.length <= 160).length}</Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Default Templates</span>
                <Badge variant="outline">{(templates || []).filter(t => t.is_active).length}</Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Custom Templates</span>
                <Badge variant="outline">{(templates || []).filter(t => !t.is_active).length}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Event Templates & Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Event Template Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="templates">Event Templates</TabsTrigger>
              <TabsTrigger value="headers-footers">Headers & Footers</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
            </TabsList>

            <TabsContent value="templates" className="mt-6">
              <NotificationTemplateList
                templates={templates || []}
                isLoading={templatesLoading}
                onEdit={handleEditTemplate}
                onDuplicate={handleDuplicateTemplate}
                onDelete={handleDeleteTemplate}
                onCreateNew={handleCreateTemplate}
              />
            </TabsContent>

            <TabsContent value="headers-footers" className="mt-6">
              <HeaderFooterManager />
            </TabsContent>

            <TabsContent value="analytics" className="mt-6">
              <NotificationAnalyticsDashboard />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Template Editor Modal */}
      <NotificationTemplateEditor
        template={editingTemplate}
        isOpen={isTemplateEditorOpen}
        onClose={handleCloseEditor}
        onSave={handleSaveTemplate}
      />

      {/* Custom Event Update Modal */}
      <CustomEventUpdateModal
        isOpen={isCustomUpdateOpen}
        onClose={() => setIsCustomUpdateOpen(false)}
        events={events}
      />


      {/* Template Type Selection Dialog */}
      <Dialog open={isTemplateTypeDialogOpen} onOpenChange={setIsTemplateTypeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Choose Template Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select the type of template you want to create or edit for "{selectedTriggerEvent.replace(/_/g, ' ')}"
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={() => handleCreateTemplateForType('email')}
                className="h-20 flex flex-col items-center gap-2"
                variant="outline"
              >
                <Mail className="h-6 w-6" />
                <span>Email Template</span>
                <span className="text-xs text-gray-500">Rich formatting, images</span>
              </Button>
              <Button
                onClick={() => handleCreateTemplateForType('sms')}
                className="h-20 flex flex-col items-center gap-2"
                variant="outline"
              >
                <MessageSquare className="h-6 w-6" />
                <span>SMS Template</span>
                <span className="text-xs text-gray-500">160 char limit</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Test Notification Dialog */}
      <Dialog open={isTestDialogOpen} onOpenChange={setIsTestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test Notification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Send a test notification for "{testingTriggerEvent.replace(/_/g, ' ')}" to verify your templates work correctly.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="testEmail">Test Email Address</Label>
                <Input
                  id="testEmail"
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="testPhone">Test Phone Number</Label>
                <Input
                  id="testPhone"
                  type="tel"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  formatPhone
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={() => sendTestNotification('email')}
                className="h-16 flex flex-col items-center gap-2"
                variant="outline"
                disabled={!testEmail}
              >
                <Mail className="h-5 w-5" />
                <span>Test Email</span>
              </Button>
              <Button
                onClick={() => sendTestNotification('sms')}
                className="h-16 flex flex-col items-center gap-2"
                variant="outline"
                disabled={!testPhone}
              >
                <MessageSquare className="h-5 w-5" />
                <span>Test SMS</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Sent
                </p>
                <p className="text-2xl font-bold">{stats.totalSent}</p>
              </div>
              <Send className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Open Rate
                </p>
                <p className="text-2xl font-bold">{stats.openRate}%</p>
              </div>
              <Mail className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Active Events
                </p>
                <p className="text-2xl font-bold">{stats.activeEvents}</p>
              </div>
              <Calendar className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Subscribers
                </p>
                <p className="text-2xl font-bold">{stats.subscribers}</p>
              </div>
              <Users className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NotificationsPage;